import type { ExecutePayloadInput, ExecuteResponse } from '../models/execute.types'
import type { InsightResult } from '../models/insight.types'
import type {
  InsightWorkbenchConfigParams,
  InsightWorkbenchResultParams,
} from '../models/insightApi.types'
import type { InsightConfig } from '../models/insightConfig.types'
import { cleanExecutePayload } from '../service/cleanExecutePayload'
import { normalizePaginatedResponse } from '../service/normalizeListResponse'

import { cleanQueryParams } from './cleanQueryParams'

import { appHttp, unwrap, type Paginated } from '@/shared/http'

function requestConfig(params: object, signal?: AbortSignal) {
  return {
    params: cleanQueryParams(params),
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

/** Sysadmin transport scoped to one selected account/workspace. */
export class InsightWorkbenchApi {
  constructor(private readonly http = appHttp) {}

  private base(accountId: number, workspaceId: number): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/insights/sysadmin`
  }

  async listConfigs(
    accountId: number,
    workspaceId: number,
    params: InsightWorkbenchConfigParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<InsightConfig>> {
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/configs/`,
      requestConfig(params, signal),
    )
    return normalizePaginatedResponse<InsightConfig>(response.data, params)
  }

  async getConfig(
    accountId: number,
    workspaceId: number,
    configId: number,
    signal?: AbortSignal,
  ): Promise<InsightConfig> {
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/configs/${configId}/`,
      requestConfig({}, signal),
    )
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async listResults(
    accountId: number,
    workspaceId: number,
    params: InsightWorkbenchResultParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<InsightResult>> {
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/results/`,
      requestConfig(params, signal),
    )
    return normalizePaginatedResponse<InsightResult>(response.data, params)
  }

  async getResult(
    accountId: number,
    workspaceId: number,
    insightId: number,
    tab?: string,
    signal?: AbortSignal,
  ): Promise<InsightResult> {
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/results/${insightId}/`,
      requestConfig({ tab }, signal),
    )
    return unwrap<InsightResult>(response)
  }

  async execute(
    accountId: number,
    workspaceId: number,
    payload: ExecutePayloadInput,
    signal?: AbortSignal,
  ): Promise<ExecuteResponse> {
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/execute/`,
      cleanExecutePayload(payload),
      requestConfig({}, signal),
    )
    return unwrap<ExecuteResponse>(response)
  }
}

export const insightWorkbenchApi = new InsightWorkbenchApi()
